import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import '../../styles/design-system.css';

/* ── Content adapted to Lucimar Bizoni's brand ── */
const accordionItems = [
  {
    id: 1,
    title: 'Ministra do Evangelho',
    description: 'Transformando vidas com a verdade bíblica',
    imageUrl: 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?q=80&w=1974&auto=format&fit=crop',
    href: '#about',
  },
  {
    id: 2,
    title: 'Educação Financeira',
    description: 'Prosperidade com princípios bíblicos',
    imageUrl: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?q=80&w=2070&auto=format&fit=crop',
    href: '#services',
  },
  {
    id: 3,
    title: 'Aconselhamento Cristão',
    description: 'Restauração da alma e da mente',
    imageUrl: 'https://images.unsplash.com/photo-1573496546038-82f9c39f6365?q=80&w=1974&auto=format&fit=crop',
    href: '#services',
  },
  {
    id: 4,
    title: 'Projeto Divas',
    description: '15 anos formando mulheres virtuosas',
    imageUrl: 'https://images.unsplash.com/photo-1556155092-490a1ba16284?q=80&w=2090&auto=format&fit=crop',
    href: '#projects',
  },
  {
    id: 5,
    title: 'Palestras & Conferências',
    description: 'Inspirando líderes em todo o Brasil',
    imageUrl: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?q=80&w=2070&auto=format&fit=crop',
    href: '#lectures',
  },
];

/* ── Single accordion card ── */
const AccordionCard = ({ item, isActive, onMouseEnter, theme }) => (
  <div
    onMouseEnter={onMouseEnter}
    style={{
      position: 'relative',
      height: 420,
      borderRadius: 20,
      overflow: 'hidden',
      cursor: 'pointer',
      flexShrink: 0,
      width: isActive ? 380 : 64,
      transition: 'width 0.65s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: isActive
        ? '0 24px 60px rgba(139,115,85,0.35)'
        : '0 4px 20px rgba(0,0,0,0.12)',
    }}
  >
    {/* Photo */}
    <img
      src={item.imageUrl}
      alt={item.title}
      onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/380x420/8B7355/ffffff?text=' + item.title; }}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.65s ease', transform: isActive ? 'scale(1.04)' : 'scale(1)' }}
    />

    {/* Gradient overlay */}
    <div style={{
      position: 'absolute', inset: 0,
      background: isActive
        ? 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.25) 50%, transparent 100%)'
        : 'linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.45) 100%)',
      transition: 'background 0.5s ease',
    }} />

    {/* Gold top accent line when active */}
    {isActive && (
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.5 }}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, var(--accent-gold-400), var(--accent-bronze-400))', transformOrigin: 'left' }}
      />
    )}

    {/* Text — horizontal when active, rotated when closed */}
    <div
      style={{
        position: 'absolute',
        bottom: isActive ? 24 : '50%',
        left: isActive ? 20 : '50%',
        transform: isActive
          ? 'none'
          : 'translate(-50%, 50%) rotate(90deg)',
        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        whiteSpace: 'nowrap',
      }}
    >
      <p style={{ color: '#E8C96A', fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4, opacity: isActive ? 1 : 0, transition: 'opacity 0.3s', fontFamily: 'SF Mono, monospace' }}>
        {isActive ? '— Saiba mais' : ''}
      </p>
      <h3 style={{ color: '#fff', fontSize: isActive ? '1.1rem' : '0.85rem', fontWeight: 600, fontFamily: "'Cormorant Garamond', serif", margin: 0 }}>
        {item.title}
      </h3>
      {isActive && (
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem', marginTop: 4, fontFamily: 'Inter, sans-serif' }}
        >
          {item.description}
        </motion.p>
      )}
    </div>
  </div>
);

/* ── Section wrapper ── */
const ImageAccordionSection = () => {
  const { theme } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(4);

  const handleNavClick = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      style={{
        background: theme === 'dark' ? '#0d0d0d' : '#FAF7F2',
        padding: '5rem 0',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Decorative blob */}
      <div style={{
        position: 'absolute', top: '-10%', right: '-5%',
        width: 500, height: 500, borderRadius: '60% 40% 55% 45%',
        background: theme === 'dark' ? 'rgba(184,134,11,0.06)' : 'rgba(184,134,11,0.08)',
        filter: 'blur(60px)', pointerEvents: 'none',
      }} />

      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '3rem' }}>

          {/* Left — text content */}
          <motion.div
            initial={{ opacity: 0, x: -32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
            style={{ flex: '1 1 300px', minWidth: 260, maxWidth: 400 }}
          >
            <span className="mono-text" style={{ color: 'var(--accent-gold-400)', display: 'block', marginBottom: '1rem' }}>
              ÁREAS DE ATUAÇÃO
            </span>

            <h2 className="heading-hero" style={{ marginBottom: '1.25rem', lineHeight: 1.15 }}>
              Transformação em todas as{' '}
              <span className="gold-shimmer">áreas da sua vida</span>
            </h2>

            <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
              Passe o mouse sobre cada área e descubra como Lucimar Bizoni pode
              te guiar através da fé e do conhecimento.
            </p>

            <motion.button
              onClick={() => handleNavClick('#contact')}
              className="btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            >
              Agende uma Consultoria
              <ArrowRight size={16} />
            </motion.button>

            {/* Active item description */}
            <motion.p
              key={activeIndex}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="body-small"
              style={{ color: 'var(--text-muted)', marginTop: '1.5rem' }}
            >
              ✦ {accordionItems[activeIndex].title} — {accordionItems[activeIndex].description}
            </motion.p>
          </motion.div>

          {/* Right — accordion */}
          <motion.div
            initial={{ opacity: 0, x: 32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
            style={{ flex: '1 1 400px', overflowX: 'auto', paddingBottom: '0.5rem' }}
          >
            <div style={{ display: 'flex', flexDirection: 'row', gap: 10, alignItems: 'center', minWidth: 'min-content' }}>
              {accordionItems.map((item, index) => (
                <AccordionCard
                  key={item.id}
                  item={item}
                  isActive={index === activeIndex}
                  onMouseEnter={() => setActiveIndex(index)}
                  theme={theme}
                />
              ))}
            </div>

            {/* Dot indicators */}
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: '1.25rem' }}>
              {accordionItems.map((_, i) => (
                <motion.div
                  key={i}
                  animate={{ width: i === activeIndex ? 24 : 6, background: i === activeIndex ? 'var(--accent-gold-400)' : 'var(--border-light)' }}
                  transition={{ duration: 0.35 }}
                  style={{ height: 6, borderRadius: 3 }}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ImageAccordionSection;
